import React, { useState, useEffect } from 'react';
import { Copy, QrCode, ExternalLink, Monitor, AlertCircle, Clock } from 'lucide-react';
import QRCodeLib from 'qrcode';
import { useTranslation } from 'react-i18next';

export default function ScreenConnectionInfo({ screen }) {
  const [qrCode, setQrCode] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (screen.connection_type === 'managed_device' && screen.registration_token && !screen.device_registered) {
      generateQRCode();
    }
  }, [screen]);

  const generateQRCode = async () => {
    try {
      const connectUrl = process.env.REACT_APP_CONNECT_URL || 'https://connect.alpenscreen.ch';
      const qrUrl = `${connectUrl}?pin=${screen.registration_token}`;
      const qrDataUrl = await QRCodeLib.toDataURL(qrUrl, {
        width: 400,
        margin: 2,
        color: { dark: '#1F2937', light: '#FFFFFF' }
      });
      setQrCode(qrDataUrl);
    } catch (error) {
      console.error('QR generation error:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.download = `alpenscreen-${screen.name}-qr.png`;
    link.href = qrCode;
    link.click();
  };

  // Managed Device - Not Registered
  if (screen.connection_type === 'managed_device' && !screen.device_registered) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 border border-blue-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 space-y-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-600 dark:bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {t('screens.awaitingRegistration')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('screens.registrationRequired')}
            </p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t('screens.registrationPIN')}
            </span>
            <button
              onClick={() => copyToClipboard(screen.registration_token)}
              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label={t('common.copy')}
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="text-3xl sm:text-4xl md:text-5xl font-mono font-bold text-white text-center py-6 sm:py-8 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 rounded-xl tracking-[0.3em] shadow-lg">
            {screen.registration_token}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 space-y-4 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">1</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{t('screens.regStep1')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">2</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 dark:text-gray-100 font-medium mb-2">{t('screens.regStep2')}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 px-3 py-2 bg-gray-50 dark:bg-gray-900/50 text-primary-600 dark:text-primary-400 text-sm rounded-lg font-mono overflow-x-auto whitespace-nowrap border border-gray-200 dark:border-gray-700">
                  connect.alpenscreen.ch
                </code>
                <button 
                  onClick={() => copyToClipboard('https://connect.alpenscreen.ch')} 
                  className="p-2 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0 border border-gray-200 dark:border-gray-700"
                  aria-label={t('common.copy')}
                >
                  <Copy className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">3</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{t('screens.regStep3')}</p>
            </div>
          </div>
        </div>

        {qrCode && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('screens.quickConnect')}</span>
              </div>
              <button 
                onClick={() => setShowQrModal(true)} 
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-left sm:text-right font-medium"
              >
                {t('screens.viewFullSize')}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white p-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-primary-500 transition-colors flex-shrink-0" onClick={() => setShowQrModal(true)}>
                <img 
                  src={qrCode} 
                  alt="QR Code" 
                  className="w-32 h-32"
                />
              </div>
              <div className="flex-1 w-full text-center sm:text-left">
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">{t('screens.scanQR')}</p>
                <button 
                  onClick={downloadQRCode} 
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 flex items-center gap-2 mx-auto sm:mx-0 font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('common.download')}
                </button>
              </div>
            </div>
          </div>
        )}

        {showQrModal && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" 
            onClick={() => setShowQrModal(false)}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 max-w-md w-full" 
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 text-center">
                {t('screens.qrCode')}
              </h3>
              <div className="bg-white p-4 sm:p-6 rounded-xl mb-6">
                <img src={qrCode} alt="QR Code" className="w-full" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={downloadQRCode} 
                  className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                  {t('common.download')}
                </button>
                <button 
                  onClick={() => setShowQrModal(false)} 
                  className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {copied && (
          <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-slide-up">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t('common.copied')}
          </div>
        )}
      </div>
    );
  }

  // Managed Device - Registered
  if (screen.connection_type === 'managed_device' && screen.device_registered) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg p-4 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-600 dark:bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-green-800 dark:text-green-200 mb-1">
              {t('screens.deviceRegistered')}
            </h3>
            <p className="text-sm text-green-700 dark:text-green-100">
              {t('screens.activeAndConnected')}
            </p>
          </div>
        </div>
        {screen.last_seen_at && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mt-4 pt-4 border-t border-green-200 dark:border-green-800/50">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{t('screens.lastSeen')}: {new Date(screen.last_seen_at).toLocaleString()}</span>
          </div>
        )}
      </div>
    );
  }

  // Direct URL
  if (screen.connection_type === 'direct_url') {
    const playerUrl = `${window.location.origin}/player/${screen.uuid}`;
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 space-y-4">
        <div className="flex items-start gap-3">
          <Monitor className="w-6 h-6 text-primary-600 dark:text-primary-400 mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {t('screens.directUrlConnection')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('screens.permanentLink')}
            </p>
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
            {t('screens.playerUrl')}
          </label>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <code className="flex-1 min-w-0 px-3 py-2 bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 text-sm rounded font-mono overflow-x-auto whitespace-nowrap border border-gray-200 dark:border-gray-700">
              {playerUrl}
            </code>
            <div className="flex gap-2">
              <button 
                onClick={() => copyToClipboard(playerUrl)} 
                className="flex-1 sm:flex-none p-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded border border-gray-200 dark:border-gray-700 transition-colors"
                aria-label={t('common.copy')}
              >
                <Copy className="w-4 h-4 text-gray-700 dark:text-gray-300 mx-auto" />
              </button>
              <a 
                href={playerUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex-1 sm:flex-none p-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded border border-gray-200 dark:border-gray-700 transition-colors"
                aria-label={t('screens.openInNewTab')}
              >
                <ExternalLink className="w-4 h-4 text-gray-700 dark:text-gray-300 mx-auto" />
              </a>
            </div>
          </div>
        </div>
        {copied && (
          <div className="fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-slide-up">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {t('common.copied')}
          </div>
        )}
      </div>
    );
  }

  return null;
}